/* eslint-disable @typescript-eslint/non-nullable-type-assertion-style */
import React, { useState, useEffect } from 'react';
import {
  Button,
  Spin,
  Input,
  Form,
  type DatePickerProps,
  DatePicker,
} from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { type AppDispatch } from '../../../redux/store';
import { type DepositClose, type DepositResponse } from '../store/depositTypes';
import { depositCloseAction } from '../store/depositActions';
import dayjs, { type Dayjs } from 'dayjs';

interface Props {
  depositDetail: DepositResponse;
  closeModalNOOP: () => void;
}

export default function DepositCloseModal({
  depositDetail,
  closeModalNOOP,
}: Props): JSX.Element {
  const [datePickerDate, setDatePickerDate] = useState<Dayjs | null>(dayjs());
  const [onlinePrincipal, setOnlinePrincipal] = useState<number>(0);
  const [offlinePrincipal, setOfflinePrincipal] = useState<number>(0);
  const [isValid, setIsValid] = useState<boolean>(false);

  const [form] = Form.useForm();

  const onChangeDate: DatePickerProps['onChange'] = (
    date: Dayjs | null,
    dateString: string
  ): void => {
    setDatePickerDate(date);
    form.setFieldsValue({ transactionDate: dateString });
  };

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const dispatch = useDispatch<AppDispatch>();

  // Validate that online + offline = pending principal
  useEffect(() => {
    const total = onlinePrincipal + offlinePrincipal;
    const pendingPrincipal =
      depositDetail.remainingPrincipal ?? depositDetail.amount;
    setIsValid(total === pendingPrincipal);
  }, [
    onlinePrincipal,
    offlinePrincipal,
    depositDetail.remainingPrincipal,
    depositDetail.amount,
  ]);

  const onFinish = async (values: any) => {
    if (!isValid) {
      return;
    }

    setIsLoading(true);
    const payload: DepositClose = {
      transactionDate: values.transactionDate,
      principal: depositDetail.remainingPrincipal ?? depositDetail.amount,
      onlinePrincipal: Number(values.OnlinePrincipal),
      offlinePrincipal: Number(values.OfflinePrincipal),
      branch:
        typeof depositDetail.branch === 'string'
          ? depositDetail.branch
          : depositDetail.branch._id,
    };

    const response = await depositCloseAction(
      dispatch,
      String(depositDetail.depositNumber ?? ''),
      payload
    );

    setIsLoading(false);
    if (response !== null) {
      form.resetFields();
      closeModalNOOP();
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log('Failed:', errorInfo);
  };

  form.setFieldsValue({
    transactionDate: dayjs().format('YYYY-MM-DD'),
  });

  const pendingPrincipal =
    depositDetail.remainingPrincipal ?? depositDetail.amount;

  return (
    <div className="deposit__payment__modal">
      <div className="divider" />
      <p style={{ fontSize: 14, fontWeight: 400, marginBottom: 24 }}>
        Note: To close a deposit, the entire principal amount must be settled in
        one transaction.
        <br /> Partial principal payments are not allowed for deposits.
      </p>
      <Form
        form={form}
        name="close_form"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
      >
        <Spin spinning={isLoading} fullscreen />
        <div className="payment__form">
          <Form.Item>
            <div className="payment__input__container">
              <p className="payment__input__label">Customer Name</p>
              <Input
                value={depositDetail?.customerName}
                disabled
                className="payment__input"
                placeholder="Customer Name"
              />
            </div>
          </Form.Item>
          <Form.Item>
            <div className="payment__input__container">
              <p className="payment__input__label">Pending Principal</p>
              <Input
                type="number"
                disabled
                value={pendingPrincipal}
                className="payment__input"
                placeholder="Pending Principal"
              />
            </div>
          </Form.Item>
          <Form.Item
            name="OnlinePrincipal"
            rules={[
              { required: true, message: 'Please enter online principal' },
            ]}
          >
            <div className="payment__input__container">
              <p className="payment__input__label">Online Principal</p>
              <Input
                type="number"
                onWheel={(e) => {
                  e.currentTarget.blur();
                }}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setOnlinePrincipal(isNaN(value) ? 0 : value);
                }}
                className="payment__input"
                placeholder="Enter online principal"
              />
            </div>
          </Form.Item>
          <Form.Item
            name="OfflinePrincipal"
            rules={[
              { required: true, message: 'Please enter offline principal' },
            ]}
          >
            <div className="payment__input__container">
              <p className="payment__input__label">Offline Principal</p>
              <Input
                type="number"
                onWheel={(e) => {
                  e.currentTarget.blur();
                }}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setOfflinePrincipal(isNaN(value) ? 0 : value);
                }}
                className="payment__input"
                placeholder="Enter offline principal"
              />
            </div>
          </Form.Item>
          {!isValid && (onlinePrincipal > 0 || offlinePrincipal > 0) && (
            <div
              style={{ color: 'red', marginBottom: 16, textAlign: 'center' }}
            >
              Online + Offline must equal Pending Principal ({pendingPrincipal})
            </div>
          )}
          <Form.Item
            name="transactionDate"
            rules={[
              { required: true, message: 'Please select transaction date!' },
            ]}
          >
            <div className="payment__input__container">
              <p className="payment__input__label">Transaction Date</p>
              <DatePicker
                value={datePickerDate}
                className="payment__input"
                onChange={onChangeDate}
              />
            </div>
          </Form.Item>
        </div>
        <div className="payment__actions__section">
          <Button
            onClick={() => {
              closeModalNOOP();
            }}
            className="payment__cancel__button"
          >
            Cancel
          </Button>
          <Button
            htmlType="submit"
            className="payment__save__button"
            disabled={!isValid}
          >
            {isLoading ? (
              <Spin
                indicator={
                  <LoadingOutlined
                    style={{ fontSize: 24, color: 'white' }}
                    spin
                  />
                }
              />
            ) : (
              <span>Close</span>
            )}
          </Button>
        </div>
      </Form>
    </div>
  );
}
